import { createServerFn } from "@tanstack/react-start";
import { supabaseAnon } from "@/lib/mcp/supabase";

/**
 * The previous version of the site published stories under /news/, /editor-choice/
 * and /press-releases/. Those articles were never carried over, so the old links are
 * dead. Rather than serving a bare 404 to visitors and search engines, resolve each
 * old slug to the closest current story and redirect permanently.
 */

const STOPWORDS = new Set([
  "a", "an", "the", "and", "or", "of", "to", "in", "on", "for", "with", "is", "are",
  "as", "at", "by", "from", "it", "its", "this", "that", "be", "how", "what", "why",
  "s", "was", "were", "will", "into", "your", "you", "new",
]);

const parseSlug = (data: unknown): string => {
  const value = typeof data === "string" ? data : (data as { slug?: unknown })?.slug;
  if (typeof value !== "string" || !value.trim()) throw new Error("slug is required");
  return value.trim().slice(0, 300).toLowerCase();
};

// Drop a trailing plural "s" so "scams" still matches a headline saying "scam".
const stem = (w: string): string => (w.length > 4 && w.endsWith("s") ? w.slice(0, -1) : w);

const words = (slug: string): string[] =>
  slug
    .replace(/[^a-z0-9]+/gi, " ")
    .toLowerCase()
    .split(" ")
    .filter((w) => w.length > 2 && !STOPWORDS.has(w))
    .map(stem);

const escapeOr = (value: string) => value.replace(/[,()]/g, " ");

export const resolveLegacyArticle = createServerFn({ method: "GET" })
  .inputValidator(parseSlug)
  .handler(async ({ data: slug }): Promise<string | null> => {
    try {
      const db = supabaseAnon();

      // 1. Same slug already exists on the current site.
      const { data: exact } = await db
        .from("articles")
        .select("slug,id")
        .eq("slug", slug)
        .limit(1)
        .maybeSingle();
      if (exact) return (exact as any).slug || (exact as any).id;

      // 2. The slug was renamed at some point.
      const { data: legacy } = await db
        .from("article_slug_history")
        .select("article_id")
        .eq("old_slug", slug)
        .limit(1)
        .maybeSingle();
      if (legacy) {
        const { data: article } = await db
          .from("articles")
          .select("slug,id")
          .eq("id", (legacy as any).article_id)
          .limit(1)
          .maybeSingle();
        if (article) return (article as any).slug || (article as any).id;
      }

      // 3. Closest current story by headline keywords. Pull a candidate set per
      // distinctive term (a single broad OR would just return the newest stories
      // matching a common word such as "crypto"), then score every candidate.
      const terms = words(slug);
      if (terms.length < 2) return null;
      const probes = [...terms].sort((a, b) => b.length - a.length).slice(0, 3);

      const candidates: any[] = [];
      for (const term of probes) {
        const { data } = await db
          .from("articles")
          .select("id,slug,title,published_at")
          .eq("is_published", true)
          .ilike("title", `%${escapeOr(term)}%`)
          .order("published_at", { ascending: false })
          .limit(50);
        if (data) candidates.push(...data);
      }

      if (candidates.length === 0) return null;


      let best: { ref: string; score: number } | null = null;
      for (const row of candidates as any[]) {
        const title = String(row.title || "").toLowerCase();
        const hits = terms.filter((t) => title.includes(t)).length;
        const score = hits / terms.length;
        if (!best || score > best.score) {
          best = { ref: row.slug || row.id, score };
        }
      }

      // Require a solid overlap so a weak match never hijacks an old link.
      if (best && best.score >= 0.55) return best.ref;
      return null;
    } catch {
      return null;
    }
  });
